import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useProjectLaunchStatus } from "@/utils/devServerUtils";
import { JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { decompressHtml } from "@/utils/navTreeCompression";
import { Database } from "@/utils/supabase.types";
import { STORYBOARDS_FOLDER, useCanvasId } from "@/utils/utils";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

interface ElementMetadata {
  hash: string;
  previewUrl?: string;
  darkPreviewUrl?: string;
  error?: boolean;
  props?: object; // the props passed into the component for the preview ie (children, className, etc)
  state?: object; //not used for now
  htmlForInstantUpdate?: string;
  compressedNavTreeForInstantUpdate?: string;
  width?: number;
  height?: number;
}

type DBElementMetadata =
  Database["public"]["Tables"]["element_metadata"]["Row"];
interface ElementMetadataContextType {
  metadata: Partial<Record<string, ElementMetadata>>;
  ungeneratedCount: number;
  ungeneratedElements: string[];
}

const ElementMetadataContext = createContext<ElementMetadataContextType>({
  metadata: {},
  ungeneratedCount: 0,
  ungeneratedElements: [],
});

interface Props {
  children: React.ReactNode;
  framework?: string;
}

interface RootState {
  supabaseStore: {
    elementMetadata: Record<string, DBElementMetadata>;
    elementMetadataLoadStatus: string;
  };
}

const processMetadataFromDb = (
  metadata: Database["public"]["Tables"]["element_metadata"]["Row"],
): ElementMetadata => {
  const htmlForInstantUpdate =
    decompressHtml(metadata.html_for_instant_update) || undefined;
  const compressedNavTreeForInstantUpdate =
    metadata.nav_tree_for_instant_update || undefined;

  return {
    hash: metadata.hash,
    previewUrl: metadata.previewUrl || undefined,
    darkPreviewUrl: metadata.darkPreviewUrl || metadata.previewUrl || undefined,
    error: Boolean(metadata.error),
    props: metadata.props as object,
    state: metadata.state as object,
    htmlForInstantUpdate,
    compressedNavTreeForInstantUpdate,
    width: metadata.width ?? undefined,
    height: metadata.height ?? undefined,
  };
};

export const ElementMetadataProvider = React.memo(
  ({ children, framework }: Props) => {
    const [metadata, setMetadata] = useState<Record<string, ElementMetadata>>(
      {},
    );

    const canvasId = useCanvasId();

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup,
    );
    const projectLaunchStatus = useProjectLaunchStatus();

    const projectElementMetadata = useSelector((state: RootState) => {
      return state.supabaseStore.elementMetadata;
    });
    const projectElementMetadataLoaded =
      useSelector((state: RootState) => {
        return state.supabaseStore.elementMetadataLoadStatus === "succeeded";
      }) && canvasId;

    const hashToElementMetadata = useMemo(() => {
      const newMap: Record<string, ElementMetadata> = {};
      if (projectElementMetadata) {
        for (const elementMetadata of Object.values(projectElementMetadata)) {
          if (elementMetadata.hash) {
            newMap[elementMetadata.hash] =
              processMetadataFromDb(elementMetadata);
          }
        }
      }
      return newMap;
    }, [projectElementMetadata]);

    const validElementsCodebaseIds = useMemo(() => {
      if (projectLaunchStatus !== "succeeded") return [];

      return Object.keys(treeElementLookup)?.filter((codebaseId: string) => {
        const element = treeElementLookup[codebaseId];
        const filenameWithoutExtension = element.filename.replace(
          /\.[^/.]+$/,
          "",
        );

        // Only allow stories
        if (element.type !== "storybook-component") {
          return false;
        }

        // Exclude Next JS app file
        if (
          (framework === "NEXT_JS" || framework === "REMIX") &&
          filenameWithoutExtension.endsWith("pages/_app")
        ) {
          return false;
        }

        // No unsaved components
        if (element.filename.includes(STORYBOARDS_FOLDER)) {
          return false;
        }

        return true;
      });
    }, [framework, projectLaunchStatus, treeElementLookup]);

    const validUngeneratedElements = useMemo(() => {
      if (!validElementsCodebaseIds?.length) return [];
      if (!projectElementMetadataLoaded) return [];

      const filtered = validElementsCodebaseIds?.filter(
        (codebaseId: string) => {
          const element = treeElementLookup[codebaseId];
          const fileHash = element?.fileHash;

          if (!fileHash) {
            return false;
          }

          if (element.type !== JsxElementType.StorybookComponent) {
            return false;
          }

          // Already generated
          if (hashToElementMetadata[fileHash]) {
            return false;
          }

          // Let useMetadataForElements handle preview existence checking
          // Just include all elements that don't have metadata
          return true;
        },
      );

      // Group by component and prioritize first variants first
      const componentGroups: { [filename: string]: string[] } = {};

      // Group elements by their filename (component file)
      for (const codebaseId of filtered) {
        const element = treeElementLookup[codebaseId];
        const filename = element?.filename;
        if (filename) {
          if (!componentGroups[filename]) {
            componentGroups[filename] = [];
          }
          componentGroups[filename].push(codebaseId);
        }
      }

      const firstVariants: string[] = [];
      const remainingVariants: string[] = [];

      // For each component group, pick the first variant and add rest to remaining
      for (const group of Object.values(componentGroups)) {
        if (group.length === 0) continue;

        // First element goes to first variants, rest go to remaining
        firstVariants.push(group[0]);
        remainingVariants.push(...group.slice(1));
      }

      // Return first variants first, then remaining variants
      return [...firstVariants, ...remainingVariants];
    }, [
      validElementsCodebaseIds,
      treeElementLookup,
      projectElementMetadataLoaded,
      hashToElementMetadata,
    ]);

    useEffect(() => {
      if (!projectElementMetadata) {
        return;
      }

      const newMetadata = Object.values(projectElementMetadata)
        .map(processMetadataFromDb)
        .reduce(
          (acc, curr) => {
            acc[curr.hash] = curr;
            return acc;
          },
          {} as Record<string, ElementMetadata>,
        );

      setMetadata((prev) => ({ ...prev, ...newMetadata }));
    }, [projectElementMetadata]);

    const contextValue = useMemo(
      () => ({
        metadata,
        ungeneratedCount: validUngeneratedElements.length,
        ungeneratedElements: validUngeneratedElements,
      }),
      [metadata, validUngeneratedElements],
    );

    return (
      <ElementMetadataContext.Provider value={contextValue}>
        {children}
      </ElementMetadataContext.Provider>
    );
  },
);

export const useElementMetadata = (): ElementMetadataContextType => {
  const context = useContext(ElementMetadataContext);
  if (!context) {
    throw new Error(
      "useElementMetadata must be used within an ElementMetadataProvider",
    );
  }
  return context;
};
